export class EncryptAdapter {
    constructor(private readonly publicKey: string) { }

    private async encryptAES(input: Uint8Array) {
        const aesKey = await crypto.subtle.generateKey(
            { name: "AES-GCM", length: 256 },
            true,
            ["encrypt"]
        );

        const iv = crypto.getRandomValues(new Uint8Array(12));

        const encryptedBuffer = await crypto.subtle.encrypt(
            {
                name: "AES-GCM",
                iv: iv
            },
            aesKey,
            input
        );

        return {
            encryptedBuffer,
            aesKey,
            iv,
        }
    }

    private async importRsaPublicKey(pem: string) {
        const binaryDer = this.stringToArrayBuffer(this.extractDerBase64FromPem(pem));
        return await crypto.subtle.importKey(
            "spki",
            binaryDer,
            {
                name: "RSA-OAEP",
                hash: "SHA-1" // compatível com backend antigo
            },
            false,
            ["encrypt"]
        );
    }

    private extractDerBase64FromPem(pem: string) {
        return atob(pem.replace(/-----[^-]+-----/g, "").replace(/\s+/g, ""));
    }

    private stringToArrayBuffer(str: string) {
        const buf = new ArrayBuffer(str.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i < str.length; i++) {
            view[i] = str.charCodeAt(i);
        }
        return buf;
    }

    private arrayBufferToBase64(buffer: ArrayBuffer) {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    private async encryptRSA(aesKey: CryptoKey, encryptedBuffer: ArrayBuffer, iv: Uint8Array) {
        const rawAesKey = await crypto.subtle.exportKey("raw", aesKey);
        const keyJson = JSON.stringify({
            key: this.arrayBufferToBase64(rawAesKey),
            iv: this.arrayBufferToBase64(iv.buffer as ArrayBuffer),
            authTag: this.arrayBufferToBase64(encryptedBuffer.slice(-16)) // last 16 bytes of AES-GCM = authTag
        });

        const publicKey = await this.importRsaPublicKey(this.publicKey);
        const encryptedKeyBuffer = await crypto.subtle.encrypt(
            {
                name: "RSA-OAEP"
            },
            publicKey,
            new TextEncoder().encode(keyJson)
        );

        const encryptedDataBase64 = this.arrayBufferToBase64(encryptedBuffer.slice(0, -16)); // remove authTag
        const encryptedKeyBase64 = this.arrayBufferToBase64(encryptedKeyBuffer);

        return {
            encryptedDataBase64,
            encryptedKeyBase64,
        }
    }

    async encrypt(input: string) {
        const data = new TextEncoder().encode(input);

        const {
            encryptedBuffer,
            aesKey,
            iv,
        } = await this.encryptAES(data);

        const {
            encryptedDataBase64,
            encryptedKeyBase64,
        } = await this.encryptRSA(aesKey, encryptedBuffer, iv);

        return {
            encryptedDataBase64,
            encryptedKeyBase64,
        }
    }
}